/**
 * ЭТАП 1 · Шаг 3 — фабрика роутера для TanStack Start (НЕ синглтон).
 * Использует РЕАЛЬНОЕ дерево роутов приложения (src/routing/routeTree.gen)
 * и те же parseSearch/stringifySearch на URLSearchParams, что src/routing/router.ts
 * (byte-for-byte как history v4 — форматы URL не меняем, условие 6).
 *
 * bindLegacyHistory(router) привязывает legacyHistory к роутеру ЭТОГО запроса.
 * На сервере роутер создаётся на каждый запрос; модульный boundRouter в
 * legacyHistory перепривязывается здесь. (Известный нюанс: при параллельных
 * SSR-запросах boundRouter — общий; строгую пер-реквестную изоляцию добавим
 * позже через getRouterInstance/ALS — на первом заходе шага 3 не критично.)
 *
 * Register-декларацию НЕ дублируем — она уже есть в src/routing/router.ts.
 */
import { createRouter } from '@tanstack/react-router';
import { QueryClient, dehydrate, hydrate } from '@tanstack/react-query';
import { isContentNotFoundError } from './api/contentNotFound';
import { routeTree } from './routing/routeTree.gen';
import { bindLegacyHistory } from './routing/legacyHistory';
import {
  queryClient as clientQueryClient,
  setQueryClient
} from './providers/query';

const parseSearch = (searchStr: string): Record<string, string> => {
  const result: Record<string, string> = {};
  for (const [key, value] of new URLSearchParams(searchStr)) {
    result[key] = value;
  }
  return result;
};

const stringifySearch = (search: Record<string, unknown>): string => {
  const params = new URLSearchParams();
  Object.entries(search).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    params.append(key, typeof value === 'string' ? value : String(value));
  });
  const str = params.toString();
  return str ? `?${str}` : '';
};

export function getRouter() {
  // ЭТАП 2 — SSR-данные: на сервере свежий QueryClient на каждый запрос
  // (кэш не делится между запросами), на клиенте — синглтон приложения.
  // dehydrate: состояние префетченных запросов сериализуется в SSR-поток;
  // hydrate: на клиенте вливается в синглтон ДО первого рендера — useQuery
  // на гидрации видит те же данные, что рендерил сервер.
  // Серверный клиент: ОДИН ретрай на транзиентные сбои и НОЛЬ на
  // CONTENT_NOT_FOUND. Раньше стояло retry:false ради быстрых 404, но тогда
  // любой единичный сбой бэка (троттлинг server->server под нагрузкой)
  // выкидывал блок из SSR-HTML целиком — прогон e2e 27.08 ловил пустые
  // списки акций/новостей и страницу без меты. Ретрай возвращает
  // устойчивость, а исключение для CONTENT_NOT_FOUND сохраняет быстрый
  // честный 404 (loader'ы роутов ждут именно этот ответ).
  const queryClient =
    typeof window === 'undefined'
      ? new QueryClient({
          defaultOptions: {
            queries: {
              retry: (failureCount, error) =>
                !isContentNotFoundError(error) && failureCount < 1,
              retryDelay: 300
            }
          }
        })
      : clientQueryClient;
  setQueryClient(queryClient);

  const router = createRouter({
    routeTree,
    parseSearch,
    stringifySearch,
    defaultPreload: false,
    scrollRestoration: true,
    dehydrate: () => ({ queryClientState: dehydrate(queryClient) }),
    hydrate: dehydrated => {
      if (dehydrated?.queryClientState) {
        hydrate(queryClient, dehydrated.queryClientState);
      }
    }
  });
  bindLegacyHistory(router);
  return router;
}
